import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';

export interface LoadingProps {
  variant?: 'circular' | 'linear' | 'dots' | 'spinner';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'inherit';
  message?: string;
  overlay?: boolean;
  fullScreen?: boolean;
  sx?: SxProps<Theme>;
}

export const Loading: React.FC<LoadingProps> = ({
  variant = 'circular',
  size = 'medium',
  color = 'primary',
  message,
  overlay = false,
  fullScreen = false,
  sx,
}) => {
  const theme = useTheme();

  // Get size values for different variants
  const getSizeValue = () => {
    const sizeMap = {
      small: { circular: 24, linear: 4 },
      medium: { circular: 40, linear: 6 },
      large: { circular: 56, linear: 8 },
    };
    return sizeMap[size];
  };

  const sizeValue = getSizeValue();

  // Render circular progress
  const renderCircular = () => (
    <CircularProgress
      color={color}
      size={sizeValue.circular}
      thickness={4}
    />
  );

  // Render linear progress
  const renderLinear = () => (
    <Box sx={{ width: '100%', maxWidth: 300 }}>
      <LinearProgress
        color={color}
        sx={{ height: sizeValue.linear, borderRadius: 2 }}
      />
    </Box>
  );

  // Render dots animation
  const renderDots = () => (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            width: size === 'small' ? 6 : size === 'large' ? 12 : 8,
            height: size === 'small' ? 6 : size === 'large' ? 12 : 8,
            borderRadius: '50%',
            backgroundColor: color === 'inherit' ? 'currentColor' : (theme.palette as any)[color].main,
            animation: 'loading-dots 1.4s ease-in-out infinite both',
            animationDelay: `${index * 0.16}s`,
            '@keyframes loading-dots': {
              '0%, 80%, 100%': {
                transform: 'scale(0)',
                opacity: 0.5,
              },
              '40%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            },
          }}
        />
      ))}
    </Box>
  );

  // Render spinner animation
  const renderSpinner = () => (
    <Box
      sx={{
        width: sizeValue.circular,
        height: sizeValue.circular,
        border: `3px solid ${theme.palette.action.disabled}`,
        borderTop: `3px solid ${color === 'inherit' ? 'currentColor' : (theme.palette as any)[color].main}`,
        borderRadius: '50%',
        animation: 'loading-spinner 1s linear infinite',
        '@keyframes loading-spinner': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />
  );

  // Get the appropriate loading component
  const getLoadingComponent = () => {
    switch (variant) {
      case 'linear':
        return renderLinear();
      case 'dots':
        return renderDots();
      case 'spinner':
        return renderSpinner();
      default:
        return renderCircular();
    }
  };

  // Container styles
  const getContainerStyles = (): SxProps<Theme> => {
    const baseStyles: SxProps<Theme> = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
    };

    if (overlay || fullScreen) {
      return {
        ...baseStyles,
        position: fullScreen ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: theme.zIndex.modal - 1,
        backdropFilter: 'blur(2px)',
      };
    }

    return {
      ...baseStyles,
      p: 2,
    };
  };

  return (
    <Box sx={[getContainerStyles(), ...(Array.isArray(sx) ? sx : [sx])]}>
      {getLoadingComponent()}
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            textAlign: 'center',
            fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default Loading;